import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ChecklistItemDto, PaginationInfo } from '../../../models/Checklist';
import { ChecklistService } from '../../../services/checklist.service';
import { ErrorService } from '../../../shared/services/error.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { TableAction } from '../../../shared/components/table/table.component';

@Component({
  selector: 'app-checklist-list',
  templateUrl: './checklist-list.component.html',
  styleUrls: ['./checklist-list.component.css']
})
export class ChecklistListComponent implements OnInit {
  title = 'Checklists';
  data: any[] = [];
  isLoading = false;
  error: string | null = null;

  // Termo de busca (usado pelo app-table)
  searchTerm: string = '';

  // Filtros
  selectedStatus = '';
  selectedType = '';

  checklistTypes = [
    { value: 'Inspeção', label: 'Inspeção' },
    { value: 'Preventiva', label: 'Preventiva' },
    { value: 'Recolhimento', label: 'Recolhimento' },
    { value: 'Corretiva', label: 'Corretiva' },
    { value: 'Qualidade', label: 'Qualidade' },
    { value: 'Comercial', label: 'Comercial' }
  ];

  statusOptions = [
    { value: 'Draft', label: 'Rascunho' },
    { value: 'InProgress', label: 'Em Progresso' },
    { value: 'Completed', label: 'Concluído' },
    { value: 'Cancelled', label: 'Cancelado' }
  ];

  // Paginação (estado do componente)
  totalCount = 0;                // Total de registros (para exibir no título/contador)
  currentPage = 1;
  pageSize = 10;
  totalPages = 0;
  hasNextPage = false;
  hasPreviousPage = false;
  
  columns = [
    { titulo: 'ID', label: 'id', formatter: (value: any) => `#${value}` },
    { titulo: 'Placa', label: 'plate' },
    { titulo: 'Veículo', label: 'vehicleInfo' },
    { titulo: 'Tipo / Subtipo', label: 'typeInfo' },
    { titulo: 'Status', label: 'statusDisplay' },
    { titulo: 'Data / Hora', label: 'dateTimeDisplay', formatter: (value: any) => {
      if (!value || value === '-') return '-';
      // Se já contém <br>, retornar como está (será tratado como HTML)
      if (value.includes('-')) return value;
      // Caso contrário, retornar valor simples
      return value;
    }},
    { titulo: 'Condutor', label: 'driverName' },
    { titulo: 'Setor', label: 'sectorDisplay' },
    { titulo: 'Informações', label: 'infoDisplay' }
  ];
  actions: TableAction[] = [];

  constructor(
    private service: ChecklistService,
    private router: Router,
    private errorService: ErrorService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.loadChecklists();
  }

  /**
   * Carrega os checklists do novo endpoint com paginação
   */
  loadChecklists(page: number = 1, pageSize: number = 10): void {
    this.isLoading = true;
    this.error = null;
    this.currentPage = page;
    this.pageSize = pageSize;

    const term = (this.searchTerm || '').toString().trim();
    const hasTypeFilter = !!this.selectedType;
    const effectivePageSize = hasTypeFilter ? 1000 : pageSize;
    const effectivePage = hasTypeFilter ? 1 : page;
    const statusParam = this.selectedStatus || undefined;
    const request$ = term
      ? this.service.getAllByPlateNumber(term, effectivePage, effectivePageSize, statusParam)
      : this.service.getAll(effectivePage, effectivePageSize, statusParam);

    request$.subscribe({
      next: (response) => {
        if (response.success && response.data) {
          // Backend com paginação nova
          if (response.pagination) {
            const pagination = response.pagination;
            // Seguir o mesmo padrão de paginação do componente de staff
            this.totalCount = pagination.total ?? 0;
            this.totalPages = pagination.totalPages ?? 0;
            this.currentPage = pagination.page ?? page;
            this.pageSize = pagination.pageSize ?? pageSize;
            this.hasNextPage = this.currentPage < this.totalPages;
            this.hasPreviousPage = this.currentPage > 1;
          } else {
            // Fallback para estrutura antiga (sem paginação real do backend):
            this.totalCount = response.count || response.data.length;
            this.totalPages = Math.ceil(this.totalCount / this.pageSize) || 0;
            this.hasNextPage = this.currentPage < this.totalPages;
            this.hasPreviousPage = this.currentPage > 1;
          }
          
          // Mapear dados de PascalCase para camelCase e formatar para exibição
          this.data = response.data.map((item: ChecklistItemDto) => {
            // Buscar data e hora dos itens do checklist (template IDs 25 e 26)
            const dateFromItem = this.findItemValueByTemplateId(item.Sections, 25);
            const timeFromItem = this.findItemValueByTemplateId(item.Sections, 26);
            
            // Formatação de data e hora
            let formattedDate = '-';
            let formattedTime = '-';
            
            // Se tiver data e hora dos itens, usar eles
            if (dateFromItem && timeFromItem) {
              try {
                let date: Date | null = null;
                
                // Tentar parsear data (pode estar em formato "DD/MM/YYYY" ou "YYYY-MM-DD")
                let dateStr = dateFromItem.trim();
                let timeStr = timeFromItem.trim();
                
                // Se a data contém "/", está no formato DD/MM/YYYY
                if (dateStr.includes('/')) {
                  const [day, month, year] = dateStr.split('/');
                  // Se a hora contém ":", está no formato HH:mm
                  if (timeStr.includes(':')) {
                    const [hour, minute] = timeStr.split(':');
                    if (day && month && year && hour && minute) {
                      date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute));
                    }
                  }
                } 
                // Se a data contém "-", está no formato YYYY-MM-DD
                else if (dateStr.includes('-')) {
                  const [year, month, day] = dateStr.split('-');
                  if (timeStr.includes(':')) {
                    const [hour, minute] = timeStr.split(':');
                    if (day && month && year && hour && minute) {
                      date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute));
                    }
                  }
                }
                
                if (date && !isNaN(date.getTime())) {
                  // Ajustar -3 horas (fuso horário)
                  date.setHours(date.getHours() - 3);
                  
                  formattedDate = date.toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  });
                  formattedTime = date.toLocaleTimeString('pt-BR', { 
                    hour: '2-digit', 
                    minute: '2-digit',
                    hour12: false
                  });
                }
              } catch (e) {
                // Usar valor padrão em caso de erro de formatação
              }
            }
            
            // Fallback: usar ChecklistDate se não tiver nos itens
            if (formattedDate === '-' && item.ChecklistDate) {
              try {
                const date = new Date(item.ChecklistDate);
                if (!isNaN(date.getTime())) {
                  // Ajustar -3 horas (fuso horário)
                  date.setHours(date.getHours() - 3);
                  
                  formattedDate = date.toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  });
                  formattedTime = date.toLocaleTimeString('pt-BR', { 
                    hour: '2-digit', 
                    minute: '2-digit',
                    hour12: false
                  });
                }
              } catch (e) {
                // Usar valor padrão em caso de erro de formatação
              }
            }

            const statusLabel = this.formatStatus(item.Status);
            const statusBadgeClass = this.getStatusBadgeClass(item.Status);
            const statusIcon = this.getStatusIcon(item.Status);
            
            // Placa (template 1) e quilometragem (template 3)
            let plateFromItems = this.findItemValueByTemplateId(item.Sections, 1);
            
            // Se não encontrou pelo template ID, tentar buscar por nome do item
            if (!plateFromItems && item.Sections && Array.isArray(item.Sections)) {
              for (const section of item.Sections) {
                if (section.Items && Array.isArray(section.Items)) {
                  const plateItem = section.Items.find((i: any) => 
                    i.ChecklistTemplateItemId === 1 || 
                    (i.ItemName && (i.ItemName.toLowerCase().includes('placa') || i.ItemName === 'Placa do Veículo'))
                  );
                  if (plateItem && plateItem.TextValue) {
                    plateFromItems = plateItem.TextValue;
                    break;
                  }
                }
              }
            }
            
            // Fallback para VehiclePlate se não encontrou nos itens
            if (!plateFromItems) {
              plateFromItems = item.VehiclePlate || null;
            }
            
            // Garantir que sempre tenha um valor
            const finalPlate = plateFromItems || 'N/A';
            
            const mileageFromItems = this.findItemNumberValueByTemplateId(item.Sections, 3) || item.Mileage || 0;
            
            return {
              id: item.Id,
              plate: finalPlate,
              checklistType: item.ChecklistType || 'N/A',
              checklistSubType: item.ChecklistSubType || 'N/A',
              status: item.Status,
              statusLabel: statusLabel,
              checklistDate: formattedDate,
              checklistTime: formattedTime,
              checklistDateTime: formattedDate !== '-' ? `${formattedDate} ${formattedTime}` : '-',
              driverName: item.DriverName || 'N/A',
              sector: item.Sector || 'N/A',
              mileage: mileageFromItems,
              fuelLevel: item.FuelLevel || 'N/A',
              tireCondition: item.TireCondition || 'N/A',
              isCompleted: item.IsCompleted,
              // Campos formatados para exibição na tabela genérica
              // Placa já está em 'plate' acima
              vehicleInfo: mileageFromItems > 0 ? `${mileageFromItems.toLocaleString('pt-BR')} km` : '-',
              typeInfo: `${item.ChecklistType || 'N/A'} / ${item.ChecklistSubType || 'N/A'}`,
              statusDisplay: statusLabel,
              dateTimeDisplay: formattedDate !== '-' ? `${formattedDate}${formattedTime !== '-' ? '-' + formattedTime : ''}` : '-',
              // driverName já está mapeado acima
              sectorDisplay: item.Sector || 'N/A',
              infoDisplay: this.formatInfoBadgesText(item.FuelLevel || 'N/A', item.TireCondition || 'N/A'),
              // Campos auxiliares para formatação
              statusBadgeClass: statusBadgeClass,
              statusIcon: statusIcon,
              _actions: this.buildRowActions(item.Status, finalPlate),
              _original: item
            };
          });
          if (hasTypeFilter) {
            this.data = this.data.filter(item => item.checklistType === this.selectedType);
            this.totalCount = this.data.length;
            this.hasNextPage = false;
            this.hasPreviousPage = false;
          }
        } else {
          this.error = response.message || 'Erro ao carregar checklists';
          this.data = [];
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Erro ao carregar checklists:', err);
        this.error = 'Erro ao carregar checklists. Tente novamente.';
        this.data = [];
        this.isLoading = false;
      }
    });
  }

  /**
   * Recebe o termo de busca do app-table e refaz a consulta no backend.
   * Obs.: o app-table já aplica debounce; aqui só reiniciamos a paginação.
   */
  onTableSearchChange(term: string): void {
    this.searchTerm = (term || '').toString();
    this.currentPage = 1;
    this.loadChecklists(this.currentPage, this.pageSize);
  }

  /**
   * Busca um valor de item por template ID nas sections
   */
  private findItemValueByTemplateId(sections: any[] | undefined, templateId: number): string | null {
    if (!sections || !Array.isArray(sections)) return null;
    for (const section of sections) {
      if (section.Items && Array.isArray(section.Items)) {
        const item = section.Items.find((i: any) => 
          i.ChecklistTemplateItemId === templateId || 
          i.checklistTemplateItemId === templateId ||
          i.TemplateItemId === templateId ||
          i.templateItemId === templateId
        );
        if (item) {
          // Tentar diferentes variações de propriedades para TextValue
          const textValue = item.TextValue || item.textValue || item.Value || item.value || null;
          if (textValue) {
            return String(textValue);
          }
        }
      }
    }
    return null;
  }

  /**
   * Busca um valor numérico de item por template ID nas sections
   */
  private findItemNumberValueByTemplateId(sections: any[] | undefined, templateId: number): number | null {
    if (!sections || !Array.isArray(sections)) return null;
    for (const section of sections) {
      if (section.Items && Array.isArray(section.Items)) {
        const item = section.Items.find((i: any) => i.ChecklistTemplateItemId === templateId);
        if (item && item.NumberValue !== null && item.NumberValue !== undefined) {
          return item.NumberValue;
        }
      }
    }
    return null;
  }

  /**
   * Formata o status para exibição
   */
  formatStatus(status: string): string {
    const statusMap: { [key: string]: string } = {
      'Draft': 'Rascunho',
      'InProgress': 'Em Progresso',
      'Completed': 'Concluído',
      'Cancelled': 'Cancelado'
    };
    return statusMap[status] || status;
  }

  /**
   * Retorna a classe CSS do badge de status
   */
  getStatusBadgeClass(status: string): string {
    const statusClasses: { [key: string]: string } = {
      'Draft': 'badge-warning',
      'InProgress': 'badge-info',
      'Completed': 'badge-success',
      'Cancelled': 'badge-danger'
    };
    return statusClasses[status] || 'badge-secondary';
  }

  /**
   * Retorna o ícone do status
   */
  getStatusIcon(status: string): string {
    const statusIcons: { [key: string]: string } = {
      'Draft': 'fa-edit',
      'InProgress': 'fa-spinner',
      'Completed': 'fa-check-circle',
      'Cancelled': 'fa-times-circle'
    };
    return statusIcons[status] || 'fa-circle';
  }

  /**
   * Formata informações do veículo para exibição
   */
  formatVehicleInfo(plate: string, mileage: number): string {
    let html = `<div class="vehicle-info"><i class="fas fa-motorcycle text-primary"></i> <div><div class="plate">${plate}</div>`;
    if (mileage > 0) {
      html += `<small class="text-muted"><i class="fas fa-tachometer-alt"></i> ${mileage.toLocaleString('pt-BR')} km</small>`;
    }
    html += `</div></div>`;
    return html;
  }

  /**
   * Formata informações de tipo/subtipo para exibição
   */
  formatTypeInfo(type: string, subtype: string): string {
    return `<div class="type-info"><div class="type-badge"><i class="fas fa-tag"></i> ${type}</div><small class="subtype">${subtype}</small></div>`;
  }

  /**
   * Formata data/hora para exibição
   */
  formatDateTime(date: string, time: string): string {
    let html = `<div class="date-info"><i class="fas fa-calendar-alt"></i> <div><div>${date}</div>`;
    if (time !== '-') {
      html += `<small class="text-muted"><i class="fas fa-clock"></i> ${time}</small>`;
    }
    html += `</div></div>`;
    return html;
  }

  /**
   * Formata badges de informações (combustível, pneus) - versão texto
   */
  formatInfoBadgesText(fuelLevel: string, tireCondition: string): string {
    const badges: string[] = [];
    if (fuelLevel !== 'N/A') {
      badges.push(`⛽ ${fuelLevel}`);
    }
    if (tireCondition !== 'N/A') {
      badges.push(`🛞 ${tireCondition}`);
    }
    return badges.length > 0 ? badges.join(' • ') : '-';
  }

  /** Ações por linha: visualizar + cancelar (quando permitido). */
  buildRowActions(status: string, _plate: string): TableAction[] {
    const rowActions: TableAction[] = [
      {
        label: 'Visualizar',
        icon: 'fas fa-eye',
        action: 'view',
        class: 'btn-view',
        visible: true
      }
    ];
    if (this.canCancelChecklist(status)) {
      rowActions.push({
        label: 'Cancelar',
        icon: 'fas fa-ban',
        action: 'cancel',
        class: 'btn-cancel',
        visible: true
      });
    }
    return rowActions;
  }

  canCancelChecklist(status: string): boolean {
    const normalized = (status || '').trim().toLowerCase();
    return normalized !== 'cancelled' && normalized !== 'cancelado';
  }

  onActionClick(evt: { action: string, item: any }): void {
    const id = evt.item.id;
    if (!id) return;
    if (evt.action === 'view') {
      this.router.navigate(['/checklist', id]);
    } else if (evt.action === 'cancel') {
      this.confirmAndCancelChecklist(id, evt.item.plate);
    }
  }

  confirmAndCancelChecklist(id: number, plate?: string): void {
    this.confirmationService.confirmCancelChecklist(id, plate).subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }
      this.executeCancelChecklist(id);
    });
  }

  private executeCancelChecklist(id: number): void {
    this.isLoading = true;
    this.service.cancelChecklist(id).subscribe({
      next: () => {
        this.isLoading = false;
        this.errorService.showSuccessMessage('Checklist cancelado com sucesso.');
        this.loadChecklists(this.currentPage, this.pageSize);
      },
      error: (err) => {
        this.isLoading = false;
        this.errorService.handleHttpError(err, 'Erro ao cancelar o checklist.');
      }
    });
  }

  onNew(): void {
    this.openChecklistTypeModal();
  }

  /**
   * Abre modal para seleção do tipo de checklist
   */
  openChecklistTypeModal(): void {
    const checklistTypes = [
      { value: 'Inspeção', label: 'Inspeção', icon: 'fa-search', description: 'Checklist de inspeção geral do veículo' },
      { value: 'Preventiva', label: 'Preventiva', icon: 'fa-shield-alt', description: 'Manutenção preventiva do veículo' },
      { value: 'Recolhimento', label: 'Recolhimento', icon: 'fa-undo', description: 'Checklist de recolhimento de veículo' },
      { value: 'Corretiva', label: 'Corretiva', icon: 'fa-tools', description: 'Manutenção corretiva do veículo' },
      { value: 'Qualidade', label: 'Qualidade', icon: 'fa-award', description: 'Checklist de controle de qualidade' },
      { value: 'Comercial', label: 'Comercial', icon: 'fa-handshake', description: 'Checklist comercial do veículo' }
    ];

    // Obter valores das variáveis CSS do sistema
    const root = getComputedStyle(document.documentElement);
    const primaryColor = root.getPropertyValue('--primary-color').trim() || '#3A4D56';
    const primaryDark = root.getPropertyValue('--primary-dark').trim() || '#2A3D46';
    const borderColor = root.getPropertyValue('--border-color').trim() || '#e9ecef';
    const textDark = root.getPropertyValue('--text-dark').trim() || '#2c3e50';
    const textLight = root.getPropertyValue('--text-light').trim() || '#6c757d';
    const backgroundLight = root.getPropertyValue('--background-light').trim() || '#f8f9fa';
    const secondaryColor = root.getPropertyValue('--secondary-color').trim() || '#6C7B7F';
    const shadowMedium = root.getPropertyValue('--shadow-medium').trim() || 'rgba(0, 0, 0, 0.15)';

    const modalHTML = `
      <div id="checklistTypeModalBackdrop" style="
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        width: 100% !important;
        height: 100% !important;
        background: rgba(0, 0, 0, 0.5) !important;
        z-index: 10000 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        padding: 20px !important;
      ">
        <div style="
          width: 100% !important;
          max-width: 600px !important;
          background: white !important;
          border-radius: 12px !important;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2) !important;
          overflow: hidden !important;
          animation: slideDown 0.3s ease-out !important;
        ">
          <div style="
            background: linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%) !important;
            color: white !important;
            padding: 24px !important;
            text-align: center !important;
          ">
            <h2 style="margin: 0 0 8px 0 !important; font-size: 24px !important; font-weight: 600 !important;">
              <i class="fas fa-clipboard-check" style="margin-right: 8px !important;"></i>
              Novo Checklist
            </h2>
            <p style="margin: 0 !important; opacity: 0.9 !important; font-size: 14px !important;">
              Selecione o tipo de checklist que deseja criar
            </p>
          </div>
          <div style="padding: 24px !important; max-height: 60vh !important; overflow-y: auto !important;">
            <div style="display: grid !important; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)) !important; gap: 16px !important;">
              ${checklistTypes.map(type => `
                <div 
                  class="checklist-type-option" 
                  data-type="${type.value}"
                  style="
                    border: 2px solid ${borderColor} !important;
                    border-radius: 8px !important;
                    padding: 20px !important;
                    cursor: pointer !important;
                    transition: all 0.2s ease !important;
                    background: white !important;
                  "
                  onmouseover="this.style.borderColor='${primaryColor}'; this.style.transform='translateY(-2px)'; this.style.boxShadow='0 4px 12px ${shadowMedium}'"
                  onmouseout="this.style.borderColor='${borderColor}'; this.style.transform='translateY(0)'; this.style.boxShadow='none'"
                >
                  <div style="text-align: center !important;">
                    <div style="
                      width: 60px !important;
                      height: 60px !important;
                      margin: 0 auto 12px auto !important;
                      background: linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%) !important;
                      border-radius: 50% !important;
                      display: flex !important;
                      align-items: center !important;
                      justify-content: center !important;
                      color: white !important;
                      font-size: 24px !important;
                    ">
                      <i class="fas ${type.icon}"></i>
                    </div>
                    <h3 style="
                      margin: 0 0 8px 0 !important;
                      font-size: 18px !important;
                      font-weight: 600 !important;
                      color: ${textDark} !important;
                    ">${type.label}</h3>
                    <p style="
                      margin: 0 !important;
                      font-size: 13px !important;
                      color: ${textLight} !important;
                      line-height: 1.4 !important;
                    ">${type.description}</p>
                  </div>
                </div>
              `).join('')}
            </div>
          </div>
          <div style="
            padding: 16px 24px !important;
            background: ${backgroundLight} !important;
            border-top: 1px solid ${borderColor} !important;
            display: flex !important;
            justify-content: flex-end !important;
            gap: 12px !important;
          ">
            <button 
              id="cancelChecklistTypeBtn"
              style="
                padding: 10px 20px !important;
                background: ${borderColor} !important;
                color: ${textDark} !important;
                border: none !important;
                border-radius: 6px !important;
                cursor: pointer !important;
                font-size: 14px !important;
                font-weight: 500 !important;
                transition: background 0.2s ease !important;
              "
              onmouseover="this.style.background='${secondaryColor}'"
              onmouseout="this.style.background='${borderColor}'"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
      <style>
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      </style>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);

    const backdrop = document.getElementById('checklistTypeModalBackdrop');
    const cancelBtn = document.getElementById('cancelChecklistTypeBtn');
    const typeOptions = document.querySelectorAll('.checklist-type-option');

    // Fechar ao clicar no backdrop
    backdrop?.addEventListener('click', (e) => {
      if (e.target === backdrop) {
        this.closeChecklistTypeModal();
      }
    });

    // Fechar ao clicar em cancelar
    cancelBtn?.addEventListener('click', () => {
      this.closeChecklistTypeModal();
    });

    // Selecionar tipo
    typeOptions.forEach(option => {
      option.addEventListener('click', () => {
        const type = (option as HTMLElement).getAttribute('data-type');
        if (type) {
          this.closeChecklistTypeModal();
          this.router.navigate(['/checklist/new'], { queryParams: { type: type } });
        }
      });
    });
  }

  /**
   * Fecha a modal de seleção de tipo de checklist
   */
  closeChecklistTypeModal(): void {
    const backdrop = document.getElementById('checklistTypeModalBackdrop');
    if (backdrop) {
      backdrop.remove();
    }
  }

  /**
   * Manipula mudança de página na tabela genérica
   */
  onSortChange(event: { sortBy: string; sortOrder: 'asc' | 'desc' }): void {
    this.data = [...this.data].sort((a, b) => {
      const va = String((a as any)[event.sortBy] ?? '');
      const vb = String((b as any)[event.sortBy] ?? '');
      const cmp = va.localeCompare(vb, 'pt-BR', { numeric: true });
      return event.sortOrder === 'asc' ? cmp : -cmp;
    });
  }

  get backendPaginationTotal(): number {
    return this.selectedType ? 0 : this.totalCount;
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadChecklists(1, this.pageSize);
  }

  clearFilters(): void {
    this.selectedStatus = '';
    this.selectedType = '';
    this.searchTerm = '';
    this.currentPage = 1;
    this.loadChecklists(1, this.pageSize);
  }

  onTablePageChange(page: number): void {
    this.currentPage = page;
    this.loadChecklists(page, this.pageSize);
    // Scroll para o topo da tabela
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /**
   * Manipula mudança de tamanho de página na tabela genérica
   */
  onTablePageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.currentPage = 1;
    this.loadChecklists(this.currentPage, this.pageSize);
  }
}


